<template>
  <ModalFormularioV2
    :model-value="modelValue"
    :titulo="isEditing ? 'Editar Preço de IA' : 'Novo Preço de IA'"
    tamanho="lg"
    :loading="salvando"
    @update:model-value="emit('update:modelValue', $event)"
    @confirmar="salvar"
  >
    <div class="row g-3">
      <div class="col-md-6">
        <CampoSelect
          id="lstProvider"
          label="Provedor"
          v-model="item.lstProvider"
          :options="provedores"
          option-value="idListaSistemaItem"
          option-label="textoLista"
          :required="true"
          placeholder="Selecione o provedor"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="model"
          label="Modelo"
          v-model="item.model"
          :required="true"
          placeholder="Ex: gpt-4o, claude-3-opus"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="precoInputPor1KTokens"
          label="Preço Input (por 1K tokens)"
          v-model="item.precoInputPor1KTokens"
          type="decimal"
          :casas-decimais="8"
          :required="true"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="precoOutputPor1KTokens"
          label="Preço Output (por 1K tokens)"
          v-model="item.precoOutputPor1KTokens"
          type="decimal"
          :casas-decimais="8"
          :required="true"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="dataInicioVigencia">
          Data Início Vigência
          <span class="text-danger">*</span>
        </label>
        <input
          id="dataInicioVigencia"
          type="date"
          class="form-control"
          v-model="item.dataInicioVigencia"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label" for="dataFimVigencia">Data Fim Vigência</label>
        <input
          id="dataFimVigencia"
          type="date"
          class="form-control"
          v-model="item.dataFimVigencia"
        />
      </div>
    </div>

    <input type="hidden" v-model="item.xmin" />
  </ModalFormularioV2>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  itemParaEditar: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'salvo', 'show-notification'])

const metadataListasStore = useMetadataListasStore()

const isEditing = computed(() => !!props.itemParaEditar?.idIaProviderPreco)
const salvando = ref(false)
const provedores = ref([])

const modeloVazio = {
  lstProvider: '',
  model: '',
  precoInputPor1KTokens: null,
  precoOutputPor1KTokens: null,
  dataInicioVigencia: '',
  dataFimVigencia: '',
  xmin: null
}

const item = reactive({ ...modeloVazio })

watch(
  () => props.itemParaEditar,
  (novo) => {
    if (novo) {
      Object.assign(item, {
        lstProvider: novo.lstProvider || '',
        model: novo.model || '',
        precoInputPor1KTokens: novo.precoInputPor1KTokens ?? null,
        precoOutputPor1KTokens: novo.precoOutputPor1KTokens ?? null,
        dataInicioVigencia: novo.dataInicioVigencia ? novo.dataInicioVigencia.split('T')[0] : '',
        dataFimVigencia: novo.dataFimVigencia ? novo.dataFimVigencia.split('T')[0] : '',
        xmin: novo.xmin || null
      })
    } else {
      Object.assign(item, { ...modeloVazio })
    }
  },
  { immediate: true }
)

const salvar = async () => {
  // Validação básica
  if (!item.lstProvider) {
    emit('show-notification', { message: 'Selecione um provedor.', type: 'warning' })
    return
  }
  if (!item.model) {
    emit('show-notification', { message: 'Informe o modelo.', type: 'warning' })
    return
  }
  if (!item.precoInputPor1KTokens && item.precoInputPor1KTokens !== 0) {
    emit('show-notification', { message: 'Informe o preço de input.', type: 'warning' })
    return
  }
  if (!item.precoOutputPor1KTokens && item.precoOutputPor1KTokens !== 0) {
    emit('show-notification', { message: 'Informe o preço de output.', type: 'warning' })
    return
  }
  if (!item.dataInicioVigencia) {
    emit('show-notification', { message: 'Informe a data de início de vigência.', type: 'warning' })
    return
  }

  salvando.value = true
  try {
    const payload = {
      lstProvider: item.lstProvider,
      model: item.model,
      precoInputPor1KTokens: item.precoInputPor1KTokens,
      precoOutputPor1KTokens: item.precoOutputPor1KTokens,
      dataInicioVigencia: item.dataInicioVigencia,
      dataFimVigencia: item.dataFimVigencia || null
    }

    if (isEditing.value) {
      payload.xmin = item.xmin
      await api.put(`/IAProviderPrecos/${props.itemParaEditar.idIaProviderPreco}`, payload)
      emit('show-notification', { message: 'Preço atualizado com sucesso!', type: 'success' })
    } else {
      await api.post('/IAProviderPrecos', payload)
      emit('show-notification', { message: 'Preço cadastrado com sucesso!', type: 'success' })
    }

    emit('salvo')
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar preço.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    if (error.response?.status === 409) {
      msg = 'Conflito de concorrência. Recarregue os dados e tente novamente.'
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    salvando.value = false
  }
}

onMounted(async () => {
  provedores.value = await metadataListasStore.getListaLookup('CONFIG_PROVIDERS_IA', 'Sistema') || []
})
</script>
